import {
    useState, useEffect
} from 'react';
import StudioAgentPanel from './StudioAgentPanel';
import BexiLogo from './BexiLogo';

/**
 * StudioAgentDock — the BEXI surface (Phase 1).
 *  - Idle / minimized: a floating action button (fades in), bottom-right.
 *  - Visible (desktop): a right dock ~560px, or expanded to ~66vw.
 *  - Visible (mobile): a full-screen bottom sheet.
 *
 * MINIMIZE vs CLOSE. Once opened, the panel stays MOUNTED so the live chat is
 * preserved. The minus button, Escape, and a backdrop click MINIMIZE it — hide
 * the panel (kept in the DOM) and show the FAB, which brings the SAME chat back.
 * The X CLOSES it — unmounts and resets, so the next open is a fresh chat.
 * (There is no auto-loading of the last chat; past chats live in the recents
 * sidebar.)
 */
export default function StudioAgentDock() {
    // `mounted`: the panel exists in the DOM (kept alive across minimize so its
    // chat state survives). `visible`: shown vs minimized (hidden but mounted).
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const openOrRestore = () => { setMounted(true); setVisible(true); };
    const minimize = () => setVisible(false); // keep mounted → chat preserved
    const close = () => { setVisible(false); setMounted(false); setExpanded(false); };

    useEffect(() => {
        if (!visible) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') minimize(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [visible]);

    return (
        <>
            {!visible && (
                <button
                    type="button"
                    onClick={openOrRestore}
                    title={mounted ? 'Reopen BEXI' : 'Open BEXI'}
                    style={{
                        background: 'linear-gradient(135deg, #9f1239 0%, #dc2626 55%, #f43f5e 100%)',
                        boxShadow: '0 12px 40px rgba(220, 38, 38, 0.55)',
                    }}
                    className="fixed bottom-10 right-8 z-[80] flex items-center gap-2.5 rounded-full py-4 pl-4 pr-6 text-white transition hover:scale-[1.04] animate-in fade-in slide-in-from-bottom-2 duration-200"
                >
                    <BexiLogo size={28} />
                    <span className="text-base font-semibold tracking-wide">BEXI</span>
                </button>
            )}

            {/* Kept mounted once opened; hidden (not unmounted) while minimized so
                the chat state is preserved. Backdrops only render when visible. */}
            {mounted && (
                <div className={visible ? 'contents' : 'hidden'}>
                    {/* Mobile backdrop (always) + desktop backdrop (only when expanded). */}
                    <div
                        onClick={minimize}
                        className="fixed inset-0 z-[59] bg-black/30 animate-in fade-in duration-200 md:hidden"
                    />
                    {expanded && (
                        <div
                            onClick={minimize}
                            className="fixed inset-0 z-[59] hidden bg-black/30 animate-in fade-in duration-200 md:block"
                        />
                    )}

                    <div
                        className={`fixed z-[60] flex flex-col overflow-hidden bg-background shadow-2xl
                            transition-all duration-200 animate-in fade-in
                            ${expanded
                    ? 'inset-0 rounded-none slide-in-from-bottom-2'
                    : `inset-x-0 bottom-0 top-14 rounded-t-2xl slide-in-from-right-8
                                 md:inset-y-0 md:right-0 md:left-auto md:top-0 md:rounded-none md:border-l md:w-2/5`}`}
                    >
                        <StudioAgentPanel
                            onClose={close}
                            onMinimize={minimize}
                            expanded={expanded}
                            onToggleExpand={() => setExpanded((v) => !v)}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
